import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { nativeDownload } from "@/lib/mediacache";
import { TYPE_CONFIG, VAULTS, SECRET_KEYS, TODO_STATUSES, statusLabel } from "@/lib/config";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import ItemFormDialog from "@/components/ItemFormDialog";
import AccessManagerDialog from "@/components/AccessManagerDialog";
import { toast } from "sonner";

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Circle; return <C {...p} />; };

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [denied, setDenied] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const { user } = useAuth();

  const loadComments = () => api.get(`/items/${id}/comments`).then((r) => setComments(r.data)).catch(() => {});

  const load = () => api.get(`/items/${id}`)
    .then((r) => {
      setItem(r.data); setRevealed({}); setDenied(null);
      api.get("/folders", { params: { vault: r.data.vault } }).then((fr) => setFolders(fr.data)).catch(() => {});
      if (r.data.vault === "todos") loadComments();
    })
    .catch((e) => {
      if (e.response?.status === 403) setDenied("no-access");
      else if (e.response?.status === 428) setDenied("passkey");
    });
  useEffect(() => { load(); }, [id]);

  const changeStatus = async (status) => {
    setSavingStatus(true);
    try {
      const { data } = await api.patch(`/items/${id}/status`, { status });
      setItem((prev) => ({ ...prev, status: data.status }));
      toast.success(`Status set to "${statusLabel(status)}"`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const addComment = async () => {
    const text = newComment.trim();
    if (!text) return;
    setPostingComment(true);
    try {
      const { data } = await api.post(`/items/${id}/comments`, { text });
      setComments((prev) => [...prev, data]);
      setNewComment("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not post comment");
    } finally {
      setPostingComment(false);
    }
  };

  const deleteComment = async (cid) => {
    try {
      await api.delete(`/items/${id}/comments/${cid}`);
      setComments((prev) => prev.filter((c) => c.id !== cid));
    } catch { toast.error("Could not delete comment"); }
  };

  if (denied) return (
    <div className="p-8 max-w-md mx-auto text-center" data-testid="access-denied">
      <Icons.ShieldAlert className="w-10 h-10 text-amber-500 mx-auto mb-3" />
      <h2 className="font-heading text-xl font-bold">
        {denied === "passkey" ? "Passkey verification needed" : "Access restricted"}
      </h2>
      <p className="text-sm text-gray-500 mt-1">
        {denied === "passkey"
          ? "This item requires a passkey to open. Verify your passkey (or register one in Settings) and try again."
          : "You don't have permission to view this item. Ask the owner or an admin for access."}
      </p>
      <div className="flex gap-2 justify-center mt-4">
        {denied === "passkey" && <Button onClick={load} data-testid="retry-passkey">Try again</Button>}
        <Button variant="outline" onClick={() => navigate(-1)}>Go back</Button>
      </div>
    </div>
  );
  if (!item) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const cfg = TYPE_CONFIG[item.type] || {};
  const vaultMeta = VAULTS[item.vault] || {};

  const reveal = async (key) => {
    if (revealed[key] != null) return setRevealed((r) => ({ ...r, [key]: null }));
    try {
      const { data } = await api.get(`/items/${id}/secret`, { params: { key } });
      setRevealed((r) => ({ ...r, [key]: data.value }));
    } catch (e) {
      if (e.response?.status === 403) toast.error("You don't have permission to reveal this secret");
      else toast.error("Could not reveal secret");
    }
  };

  const openFile = async () => {
    try {
      await nativeDownload(item.file_id, { inline: true });
    } catch (e) {
      if (e.response?.status === 403) toast.error("You don't have permission to open this file");
      else if (e.response?.status === 428) toast.error("Passkey verification required to open this file");
      else toast.error("Could not open file");
    }
  };

  const copy = async (v) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(v);
      } else {
        throw new Error("clipboard-unavailable");
      }
      toast.success("Copied");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = v;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success("Copied");
        else toast.error("Copy blocked — select and copy manually");
      } catch {
        toast.error("Copy blocked by browser — copy manually");
      }
    }
  };

  const del = async () => {
    await api.delete(`/items/${id}`);
    toast.success("Deleted");
    navigate(`/vault/${item.vault}`);
  };

  const unlink = async (targetId) => {
    await api.post(`/items/${id}/unlink`, { target_id: targetId });
    load();
  };

  const fieldEntries = Object.entries(item.fields || {}).filter(([, v]) => v !== "" && v != null);

  return (
    <div className="p-6 sm:p-8 max-w-[1100px]">
      <button onClick={() => navigate(`/vault/${item.vault}`)} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 mb-4" data-testid="back-btn">
        <Icons.ChevronLeft className="w-4 h-4" /> {vaultMeta.label}
      </button>

      <header className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-md flex items-center justify-center" style={{ background: `${vaultMeta.color}15` }}>
            <Icon name={cfg.icon || "Circle"} className="w-7 h-7" style={{ color: vaultMeta.color }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">{cfg.label || item.type}</span>
              {item.vault === "todos" && item.can_edit ? (
                <Select value={item.status} onValueChange={changeStatus} disabled={savingStatus}>
                  <SelectTrigger className="h-7 w-[150px] text-xs" data-testid="todo-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TODO_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline" data-testid="status-badge">{statusLabel(item.status)}</Badge>
              )}
              {item.require_passkey && (
                <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50" data-testid="passkey-badge">
                  <Icons.Fingerprint className="w-3 h-3 mr-1" />Passkey required
                </Badge>
              )}
              {((item.view_user_ids || []).length + (item.edit_user_ids || []).length) === 0 ? (
                <Badge variant="outline" className="text-gray-600" data-testid="private-badge">
                  <Icons.Lock className="w-3 h-3 mr-1" />Private
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[#1B4332] border-green-200 bg-green-50" data-testid="shared-badge">
                  <Icons.Users className="w-3 h-3 mr-1" />Shared
                </Badge>
              )}
            </div>
            <h1 className={`font-heading text-2xl font-extrabold tracking-tight mt-0.5 ${item.vault === "todos" && item.status === "completed" ? "line-through text-gray-400" : ""}`}>{item.title}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {[item.department, item.category].filter(Boolean).join(" · ") || "No category"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {item.can_manage && (
            <Button variant="outline" onClick={() => setAccessOpen(true)} data-testid="manage-access-btn">
              <Icons.ShieldCheck className="w-4 h-4 mr-1.5" />Access
            </Button>
          )}
          {item.can_edit && (
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-btn"><Icons.Pencil className="w-4 h-4 mr-1.5" />Edit</Button>
          )}
          {item.can_edit && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-btn"><Icons.Trash2 className="w-4 h-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
                <AlertDialogDescription>This permanently removes “{item.title}” and its relationships.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Details */}
          <section className="bg-white border border-gray-200 rounded-md">
            <div className="px-5 py-4 border-b border-gray-100"><h3 className="font-heading font-semibold">Details</h3></div>
            <div className="divide-y divide-gray-50">
              <Meta label="Owner" value={item.owner || "—"} />
              <Meta label="Expiration" value={item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : "—"} />
              {fieldEntries.map(([k, v]) => {
                const isSecret = SECRET_KEYS.includes(k);
                const label = (cfg.fields || []).find((f) => f.key === k)?.label || k;
                return (
                  <div key={k} className="px-5 py-3 flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500 capitalize">{label}</span>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium truncate max-w-[280px]">
                        {isSecret ? (revealed[k] != null ? revealed[k] : "••••••••") : v}
                      </span>
                      {isSecret && (
                        <>
                          <button onClick={() => reveal(k)} data-testid={`reveal-${k}`} className="text-gray-400 hover:text-gray-900">
                            <Icon name={revealed[k] != null ? "EyeOff" : "Eye"} className="w-4 h-4" />
                          </button>
                          <button onClick={() => reveal(k).then(() => {})} className="hidden" />
                        </>
                      )}
                      {(isSecret ? revealed[k] != null : true) && (
                        <button onClick={() => copy(isSecret ? revealed[k] : v)} className="text-gray-400 hover:text-gray-900" data-testid={`copy-${k}`}>
                          <Icons.Copy className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* File */}
          {item.file_id && (
            <section className="bg-white border border-gray-200 rounded-md p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center"><Icons.Paperclip className="w-5 h-5 text-gray-600" /></div>
                <div><div className="text-sm font-medium">Attached file</div><div className="text-xs text-gray-400">Stored securely</div></div>
              </div>
              <a href={`${API_BASE}/files/${item.file_id}/download`} onClick={(e) => { e.preventDefault(); openFile(); }} data-testid="download-file">
                <Button variant="outline"><Icons.Download className="w-4 h-4 mr-1.5" />Open</Button>
              </a>
            </section>
          )}

          {/* Notes */}
          {item.notes && (
            <section className="bg-white border border-gray-200 rounded-md">
              <div className="px-5 py-4 border-b border-gray-100"><h3 className="font-heading font-semibold">Notes</h3></div>
              <p className="px-5 py-4 text-sm text-gray-700 whitespace-pre-wrap">{item.notes}</p>
            </section>
          )}

          {/* Comments (to-dos) */}
          {item.vault === "todos" && (
            <section className="bg-white border border-gray-200 rounded-md" data-testid="comments-section">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <Icons.MessageSquare className="w-4 h-4 text-[#ea580c]" />
                <h3 className="font-heading font-semibold">Comments</h3>
                <span className="text-xs text-gray-400">{comments.length}</span>
              </div>
              <div className="divide-y divide-gray-50">
                {comments.length === 0 && (
                  <div className="px-5 py-8 text-center text-sm text-gray-400" data-testid="no-comments">No comments yet. Start the discussion below.</div>
                )}
                {comments.map((c) => (
                  <div key={c.id} className="px-5 py-3 group" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-[#ea580c]/15 text-[#ea580c] text-[11px] font-bold flex items-center justify-center shrink-0">
                          {(c.user_name || "?").charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium truncate">{c.user_name || "Unknown"}</span>
                        <span className="text-[11px] text-gray-400">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</span>
                      </div>
                      {c.can_delete && (
                        <button onClick={() => deleteComment(c.id)} data-testid={`delete-comment-${c.id}`}
                          className="text-gray-300 hover:text-red-500 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          <Icons.Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1 pl-8">{c.text}</p>
                  </div>
                ))}
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex items-end gap-2">
                <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addComment(); }}
                  placeholder="Write a comment… (⌘/Ctrl+Enter to send)" rows={2}
                  className="resize-none" data-testid="comment-input" />
                <Button onClick={addComment} disabled={postingComment || !newComment.trim()}
                  className="bg-[#ea580c] hover:bg-[#c2410c] shrink-0" data-testid="post-comment-btn">
                  <Icons.Send className="w-4 h-4" />
                </Button>
              </div>
            </section>
          )}
        </div>

        {/* Sidebar: relationships + tags */}
        <div className="space-y-6">
          <section className="bg-white border border-gray-200 rounded-md">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold flex items-center gap-2"><Icons.Share2 className="w-4 h-4 text-[#1B4332]" />Related assets</h3>
              <button onClick={() => setLinkOpen(true)} className="text-[#1B4332] hover:underline text-sm font-medium" data-testid="add-link-btn">+ Link</button>
            </div>
            <div className="divide-y divide-gray-50">
              {(item.related || []).length === 0 && <div className="px-5 py-6 text-center text-sm text-gray-400">No linked assets yet.</div>}
              {(item.related || []).map((r) => (
                <div key={r.id} className="px-5 py-3 flex items-center justify-between group">
                  <button onClick={() => navigate(`/item/${r.id}`)} className="flex items-center gap-2.5 min-w-0 text-left">
                    <Icon name={(TYPE_CONFIG[r.type] || {}).icon || "Circle"} className="w-4 h-4 text-gray-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.title}</div>
                      <div className="text-[11px] text-gray-400 capitalize">{r.type}</div>
                    </div>
                  </button>
                  <button onClick={() => unlink(r.id)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100" data-testid={`unlink-${r.id}`}>
                    <Icons.X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {(item.tags || []).length > 0 && (
            <section className="bg-white border border-gray-200 rounded-md p-5">
              <h3 className="font-heading font-semibold mb-3 text-sm">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
              </div>
            </section>
          )}
        </div>
      </div>

      <ItemFormDialog open={editOpen} onClose={() => setEditOpen(false)} vault={item.vault} item={item} folders={folders} onSaved={load} />
      <AccessManagerDialog open={accessOpen} onClose={() => setAccessOpen(false)} item={item} onSaved={load} />
      <LinkDialog open={linkOpen} onClose={() => setLinkOpen(false)} currentId={id} related={item.related || []} onLinked={load} />
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="px-5 py-3 flex items-center justify-between">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function LinkDialog({ open, onClose, currentId, related, onLinked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/items").then((r) => setResults(r.data));
  }, [open]);

  const linkedIds = new Set(related.map((r) => r.id));
  const filtered = results.filter((it) => it.id !== currentId && !linkedIds.has(it.id) &&
    (!q || it.title.toLowerCase().includes(q.toLowerCase())));

  const link = async (targetId) => {
    await api.post(`/items/${currentId}/link`, { target_id: targetId });
    toast.success("Linked");
    onLinked();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="link-dialog">
        <DialogHeader><DialogTitle className="font-heading">Link an asset</DialogTitle></DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets…" data-testid="link-search" />
        <div className="max-h-[360px] overflow-y-auto vault-scroll space-y-1 mt-2">
          {filtered.length === 0 && <div className="py-8 text-center text-sm text-gray-400">No assets to link.</div>}
          {filtered.map((it) => (
            <button key={it.id} onClick={() => link(it.id)} data-testid={`link-target-${it.id}`}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-gray-50 text-left">
              <Icon name={(TYPE_CONFIG[it.type] || {}).icon || "Circle"} className="w-4 h-4 text-gray-500" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{it.title}</div>
                <div className="text-[11px] text-gray-400 capitalize">{it.type} · {VAULTS[it.vault]?.label}</div>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
